/**
 * Stock transfers › Why — the Excel workbook, a working file for a merchandiser (owner ruling
 * DEC-104, `docs/adr/9041`). The CSV of the same export stays the machine file: every field of the
 * transfer-explanations report, one column each, codes as codes (`transferWhySheet`).
 *
 * Three sheets:
 *
 *   «Trasferimenti»   one row per size line (DEC-104's default grain), every line the planner's
 *                     filters keep, in their ranking; names beside codes, codes as words, the
 *                     decision first so the autofilter on it is the first thing to hand. The names,
 *                     brand, family, category, stock before and after, transit, four-week sales and
 *                     risk come from `GET /inseason/runs/{run_id}/report/transfers`, joined on
 *                     `transfer_id`; a line the join misses stays, its names «n.d.». The codes are
 *                     the ERP's own (DEC-106): `store_key` is Oracle's store code, `article_key` is
 *                     `ARV_ARTICLE`, verbatim. The value of the PROPOSED pairs at cost and at retail
 *                     price (DEC-106) follows «Paia decise»: proposed pairs × the report's unit cost
 *                     and × its retail price (bata-platform ADR 6130, `transfer-value-contract.ts`),
 *                     then the retail price's basis — list or ticket, VAT included or not and at
 *                     what rate — as the platform states it, never assumed. Either value is «n.d.»
 *                     with its reason in words in «Tecnico» where the platform has none
 *   «Riepilogo …»     one row per origin × destination × article — the shipment — its sizes as
 *                     columns (proposed pairs), the total, the two values, the retail basis and the
 *                     net benefit
 *   «Tecnico»         written by the shared writer: the provenance once, then every field the
 *                     business sheet does not show — ids, codes, size key/scale/position, the
 *                     recorded basis, the move cap, the supersession, every absent reason in words
 *
 * Which field means what was read in the pinned spec (`TransferExplanationReportRow`,
 * `TransferReportRow`): `units` is the line's proposed pairs (the report's `engine_units`),
 * `decided_units` the decided pairs as the transfers report reads them (D-5: absent while
 * pending), `…_on_hand_before/after` the door's stock in the size before and after the run's
 * decided moves, `…_sales_l4w_units` gross units over the 28 days before `as_of`, `risk` the
 * source's own label (`RISK_DESC`), verbatim.
 */

import type { TransferExplanationReportRow, TransferReasonOption, TransferReportRow } from '@/api/generated';
import type { ExportCell, ExportColumn, ExportTranslate, WorkbookSheet } from '@/export';
import { scalesMix, sizeName } from '@/planning';

import { toConstraintKey, toPurposeKey, UNCLASSIFIED_CONSTRAINT_KEY } from './api';
import { reasonLabelOf } from './decision-cell';
import { SUPERSEDED } from './superseded';
import {
  TRANSFER_RETAIL_PRICE, TRANSFER_UNIT_COST, VALUE_NOT_SENT, type TransferRetailPrice, type TransferUnitCost,
} from './transfer-value-contract';
import { dispositionStateKey } from './transfer-dispositions';
import {
  TRANSFER_WHY_DECIDED_BY_COLUMN, TRANSFER_WHY_FIELDS, transferDecidedByCell, transferWhyCells, type TransferDeciders,
} from './transfer-why-fields';
import { PENDING } from './transfer-ranking';
import { UNRECOGNISED_WHY_REASON_KEY, whyReasonKey } from './why';
import type { Translate } from './export-provenance';

const NOT_AVAILABLE: ExportCell = { kind: 'missing', state: 'not-reported' };
const BLANK: ExportCell = { kind: 'text', value: '' };

const text = (value: string | null | undefined): ExportCell => (
  value === null || value === undefined || value === '' ? NOT_AVAILABLE : { kind: 'text', value }
);
const integer = (value: number | null | undefined): ExportCell => (
  value === null || value === undefined ? NOT_AVAILABLE : { kind: 'integer', raw: value }
);
const number = (value: number | null | undefined): ExportCell => (
  value === null || value === undefined ? NOT_AVAILABLE : { kind: 'number', raw: value }
);

/** An absent reason as words: the catalogue's sentence, or the code itself when this build does not name it. */
export function whyReasonWords(code: string, translate: ExportTranslate): string {
  const key = whyReasonKey(code);
  return key === UNRECOGNISED_WHY_REASON_KEY ? code : translate(key);
}

/** A constraint or driver code as words; one this build does not name keeps its code beside the label. */
function constraintWords(code: string, translate: Translate): string {
  const key = toConstraintKey(code);
  return key === UNCLASSIFIED_CONSTRAINT_KEY
    ? translate('inseason.transferWhyWorkbook.reason.unrecognised', { label: translate(key), code })
    : translate(key);
}

/**
 * The line's «why» in one sentence: what bound it and what drove it, from the run's recorded basis
 * (bata-platform ADR 6127) through the constraint catalogue the Why page uses. A line written
 * before the record says why it has none. Empty when the basis names neither.
 */
export function transferReasonSentence(row: TransferExplanationReportRow, translate: Translate): string {
  const drivers = (row.basis_drivers ?? '').split('|').filter((code) => code !== '').map((code) => constraintWords(code, translate));
  const bound = row.basis_bound_by === null || row.basis_bound_by === '' ? null : constraintWords(row.basis_bound_by, translate);
  if (bound === null && drivers.length === 0) {
    return row.basis_absent_reason === null ? '' : whyReasonWords(row.basis_absent_reason, translate);
  }
  if (drivers.length === 0) return translate('inseason.transferWhyWorkbook.reason.boundBy', { boundBy: bound });
  if (bound === null) return translate('inseason.transferWhyWorkbook.reason.drivers', { drivers: drivers.join(', ') });
  return translate('inseason.transferWhyWorkbook.reason.sentence', { boundBy: bound, drivers: drivers.join(', ') });
}

/** The line's decision as the list states it: recorded, or pending, or superseded (ADR 6048). */
export function transferDecisionCode(row: TransferExplanationReportRow): string | null {
  if (row.disposition !== null) return row.disposition;
  return row.superseded_by_run_id !== null || row.disposition_absent_reason === SUPERSEDED ? SUPERSEDED : null;
}

// ── «Trasferimenti» ──────────────────────────────────────────────────────────────────────────

const column = (key: string, headerKey: string, kind: ExportColumn['kind'], extra: Partial<ExportColumn> = {}): ExportColumn => ({
  key, headerKey, kind, ...extra,
});

/** design/09 §4.3 — no decimals on an amount above 1,000; a transfer line's pesos are thousands. */
const MONEY = { decimals: 0, total: 'sum' } as const;

/** The business columns, in DEC-104's order. */
const BUSINESS_COLUMNS: readonly ExportColumn[] = [
  column('decision', 'inseason.transferWhyWorkbook.column.decision', 'label', { freeze: true, descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.decision' }),
  column('donor_store_name', 'inseason.transferWhyWorkbook.column.donorName', 'text', { freeze: true, descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.donor_store_name' }),
  column('donor_store_key', 'inseason.transferWhyWorkbook.column.donorCode', 'text', { freeze: true, descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.donor_store_key' }),
  column('recipient_store_name', 'inseason.transferWhyWorkbook.column.recipientName', 'text', { freeze: true, descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.recipient_store_name' }),
  column('recipient_store_key', 'inseason.transferWhyWorkbook.column.recipientCode', 'text', { descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.recipient_store_key' }),
  column('article_name', 'inseason.transferWhyWorkbook.column.articleName', 'text', { descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.article_name' }),
  column('article_key', 'inseason.transferWhyWorkbook.column.articleCode', 'text', { descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.article_key' }),
  column('brand', 'inseason.transferWhyWorkbook.column.brand', 'text', { descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.brand' }),
  column('department_family', 'inseason.transferWhyWorkbook.column.departmentFamily', 'text', { descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.department_family' }),
  column('source_category', 'inseason.transferWhyWorkbook.column.category', 'text', { descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.source_category' }),
  column('size_code', 'inseason.transferWhyWorkbook.column.size', 'text', { descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.size_code' }),
  column('units', 'inseason.transferWhyWorkbook.column.proposedPairs', 'integer', { total: 'sum', descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.units' }),
  column('decided_units', 'inseason.transferWhyWorkbook.column.decidedPairs', 'integer', { total: 'sum', descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.decided_units' }),
  column('value_at_cost', 'inseason.transferWhyWorkbook.column.valueAtCost', 'money', { ...MONEY, descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.value_at_cost' }),
  column('value_at_retail', 'inseason.transferWhyWorkbook.column.valueAtRetail', 'money', { ...MONEY, descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.value_at_retail' }),
  column('retail_price_basis', 'inseason.transferWhyWorkbook.column.retailPriceBasis', 'text', { descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.retail_price_basis' }),
  column('net_benefit', 'inseason.transferWhyWorkbook.column.netBenefit', 'money', { ...MONEY, polarity: 'higher-is-better', descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.net_benefit' }),
  column('lane_cost', 'inseason.transferWhyWorkbook.column.laneCost', 'money', { ...MONEY, polarity: 'lower-is-better', descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.lane_cost' }),
  column('run_damage', 'inseason.transferWhyWorkbook.column.runDamage', 'money', { ...MONEY, polarity: 'lower-is-better', descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.run_damage' }),
  column('reason', 'inseason.transferWhyWorkbook.column.reason', 'text', { descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.reason' }),
  column('donor_on_hand_before', 'inseason.transferWhyWorkbook.column.donorBefore', 'integer', { descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.donor_on_hand_before' }),
  column('donor_on_hand_after', 'inseason.transferWhyWorkbook.column.donorAfter', 'integer', { descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.donor_on_hand_after' }),
  column('donor_sales_l4w_units', 'inseason.transferWhyWorkbook.column.donorSalesL4w', 'integer', { descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.donor_sales_l4w_units' }),
  column('donor_weeks_of_cover', 'inseason.transferWhyWorkbook.column.donorCover', 'number', { decimals: 1, descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.donor_weeks_of_cover' }),
  column('recipient_on_hand_before', 'inseason.transferWhyWorkbook.column.recipientBefore', 'integer', { descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.recipient_on_hand_before' }),
  column('recipient_on_hand_after', 'inseason.transferWhyWorkbook.column.recipientAfter', 'integer', { descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.recipient_on_hand_after' }),
  column('recipient_in_transit_units', 'inseason.transferWhyWorkbook.column.recipientInTransit', 'integer', { descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.recipient_in_transit_units' }),
  column('recipient_sales_l4w_units', 'inseason.transferWhyWorkbook.column.recipientSalesL4w', 'integer', { descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.recipient_sales_l4w_units' }),
  column('recipient_weeks_of_cover', 'inseason.transferWhyWorkbook.column.recipientCover', 'number', { decimals: 1, descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.recipient_weeks_of_cover' }),
  column('risk', 'inseason.transferWhyWorkbook.column.risk', 'text', { descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.risk' }),
  column('purpose_label', 'inseason.transferWhyWorkbook.column.purpose', 'label', { descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.purpose_label' }),
  column('decision_reason', 'inseason.transferWhyWorkbook.column.decisionReason', 'text', { descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.decision_reason' }),
  column('decided_at', 'inseason.transferWhyWorkbook.column.decidedAt', 'date', { descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.decided_at' }),
  // Who decided, by name, beside when (issue 249) — as the other decision files say it.
  { ...TRANSFER_WHY_DECIDED_BY_COLUMN, key: 'decided_by' },
  column('superseded', 'inseason.transferWhyWorkbook.column.superseded', 'text', { descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.superseded' }),
];

// ── the value of the moved pairs (DEC-106) ─────────────────────────────────────────────────────

/**
 * A line's value: a raw amount in a currency — at retail, on the price's stated basis — or the code
 * saying why there is none.
 */
export type LineValue =
  | { readonly kind: 'value'; readonly raw: number; readonly currency: string; readonly basis?: RetailBasis }
  | { readonly kind: 'absent'; readonly reason: string };

/** What a retail price is, as the platform states it: its kind, and whether and at what rate it includes VAT. */
export interface RetailBasis {
  readonly kind: string;
  readonly includesVat: boolean;
  /** The VAT multiplier, decimal text as published: 1.19 for 19%. */
  readonly vatRate: string;
}

/** The unit cost the report row carries; `null` with no report row. */
function unitCostOf(report: TransferReportRow | undefined): TransferUnitCost | null {
  return report === undefined ? null : TRANSFER_UNIT_COST(report);
}

/** The retail price the report row carries; `null` with no report row. */
function retailPriceOf(report: TransferReportRow | undefined): TransferRetailPrice | null {
  return report === undefined ? null : TRANSFER_RETAIL_PRICE(report);
}

/** A field an API older than v0.59.0 does not send reads as absent, the same as `null`. */
const given = <T>(value: T | null | undefined): T | null => (value === undefined ? null : value);

/**
 * The line's PROPOSED pairs at the article's landed unit cost (bata-platform ADR 6130): `units` ×
 * `unit_cost`, in the cost's currency. Absent, never 0, with the platform's reason. `null` when the
 * line has no report row: the «n.d.» is then the join's, which «Nel report trasferimenti» already says.
 */
export function valueAtCost(row: TransferExplanationReportRow, report: TransferReportRow | undefined): LineValue | null {
  const cost = unitCostOf(report);
  if (cost === null) return null;
  const unit = given(cost.unit_cost);
  const currency = given(cost.unit_cost_currency);
  if (unit === null || currency === null) return { kind: 'absent', reason: given(cost.unit_cost_absent_reason) ?? VALUE_NOT_SENT };
  return { kind: 'value', raw: row.units * Number(unit), currency };
}

/**
 * The line's PROPOSED pairs at the article's retail price in force on the run's `as_of` (ADR 6130):
 * `units` × `retail_price`, in its currency, ON THE BASIS THE PLATFORM STATES — never converted
 * between gross and net, never assumed. A price without its VAT basis or its kind is not a value: it
 * is absent with the platform's own code for that. On v0.59.0 every row reads
 * `retail_price_not_landed` (bata-data issue 484 is the owner's open decision on the source).
 */
export function valueAtRetail(row: TransferExplanationReportRow, report: TransferReportRow | undefined): LineValue | null {
  const price = retailPriceOf(report);
  if (price === null) return null;
  const unit = given(price.retail_price);
  const currency = given(price.retail_price_currency);
  if (unit === null || currency === null) return { kind: 'absent', reason: given(price.retail_price_absent_reason) ?? VALUE_NOT_SENT };
  const includesVat = given(price.retail_price_includes_vat);
  const vatRate = given(price.retail_price_vat_rate);
  if (includesVat === null || vatRate === null) return { kind: 'absent', reason: 'retail_price_vat_basis_not_reported' };
  const kind = given(price.retail_price_kind);
  if (kind === null) return { kind: 'absent', reason: 'retail_price_kind_not_reported' };
  return { kind: 'value', raw: row.units * Number(unit), currency, basis: { kind, includesVat, vatRate } };
}

const valueCell = (value: LineValue | null): ExportCell => (
  value?.kind === 'value' ? { kind: 'money', raw: value.raw, currency: value.currency } : NOT_AVAILABLE
);
const valueReasonCell = (value: LineValue | null): ExportCell => (
  value?.kind === 'absent' ? { kind: 'text', value: value.reason } : NOT_AVAILABLE
);

/** The VAT multiplier as a percentage: 1.19 → 19, 1 → 0. `null` for a rate that is not a number. */
export function vatPercent(rate: string): number | null {
  const multiplier = Number(rate);
  return Number.isFinite(multiplier) && rate.trim() !== '' ? Math.round((multiplier - 1) * 10_000) / 100 : null;
}

/** A price kind in words; one this build does not name stays its code. */
const RETAIL_KIND_KEY: Readonly<Record<string, string>> = {
  list: 'inseason.transferWhyWorkbook.retailKind.list',
  ticket: 'inseason.transferWhyWorkbook.retailKind.ticket',
};

function retailKindWords(code: string, translate: ExportTranslate): string {
  const key = RETAIL_KIND_KEY[code];
  return key === undefined ? code : translate(key);
}

/**
 * The basis of a value at retail, in words: «Listino, IVA inclusa al 19%». Read from the platform's
 * `retail_price_kind`, `retail_price_includes_vat` and `retail_price_vat_rate`; «n.d.» when there is
 * no value, so a reader never takes a basis for granted.
 */
export function retailBasisCell(value: LineValue | null, translate: Translate): ExportCell {
  if (value?.kind !== 'value' || value.basis === undefined) return NOT_AVAILABLE;
  const { kind, includesVat, vatRate } = value.basis;
  const rate = vatPercent(vatRate);
  const words = rate === null
    ? translate(includesVat ? 'inseason.transferWhyWorkbook.retailBasis.includedRateAsPublished' : 'inseason.transferWhyWorkbook.retailBasis.excludedRateAsPublished', {
      kind: retailKindWords(kind, translate), rate: vatRate,
    })
    : translate(includesVat ? 'inseason.transferWhyWorkbook.retailBasis.included' : 'inseason.transferWhyWorkbook.retailBasis.excluded', {
      kind: retailKindWords(kind, translate), rate,
    });
  return { kind: 'text', value: words };
}

/** reqs/07 R3's cost provenance in words; a code this build does not name stays a code. */
const COST_PROVENANCE_KEY: Readonly<Record<string, string>> = {
  ACTUAL: 'inseason.transferWhyWorkbook.costProvenance.actual',
  STANDARD: 'inseason.transferWhyWorkbook.costProvenance.standard',
  MODELLED: 'inseason.transferWhyWorkbook.costProvenance.modelled',
  UNKNOWN: 'inseason.transferWhyWorkbook.costProvenance.unknown',
};

function costProvenanceWords(code: string, translate: ExportTranslate): string {
  const key = COST_PROVENANCE_KEY[code];
  return key === undefined ? code : translate(key);
}

/** The retail price's provenance in words; a code this build does not name stays a code. */
const PRICE_PROVENANCE_KEY: Readonly<Record<string, string>> = {
  SOURCE: 'inseason.transferWhyWorkbook.priceProvenance.source',
  CALCULATED: 'inseason.transferWhyWorkbook.priceProvenance.calculated',
  UNKNOWN: 'inseason.transferWhyWorkbook.priceProvenance.unknown',
};

function priceProvenanceWords(code: string, translate: ExportTranslate): string {
  const key = PRICE_PROVENANCE_KEY[code];
  return key === undefined ? code : translate(key);
}

/** Whether the price includes VAT, in words. */
function includesVatWords(code: string, translate: ExportTranslate): string {
  if (code === 'true') return translate('inseason.transferWhyWorkbook.retailIncludesVat.true');
  if (code === 'false') return translate('inseason.transferWhyWorkbook.retailIncludesVat.false');
  return code;
}

/** The unit cost's and the retail price's own fields to «Tecnico», then why each value is «n.d.». */
function valueTechnicalColumns(translate: Translate): ExportColumn[] {
  const reason = (key: string, explains: string, descriptionKey: string): ExportColumn => ({
    key, headerKey: 'inseason.transferWhyWorkbook.tech.absentReason', kind: 'text', technical: true,
    headerValues: { field: translate(explains) }, display: whyReasonWords, descriptionKey,
  });
  const tech = (key: string, headerKey: string, kind: ExportColumn['kind'], descriptionKey: string, extra: Partial<ExportColumn> = {}): ExportColumn => ({
    key, headerKey, kind, technical: true, descriptionKey, ...extra,
  });
  return [
    tech('report_unit_cost', 'inseason.transferWhyWorkbook.tech.unitCost', 'money', 'inseason.columnGuide.transferWhyWorkbook.report_unit_cost'),
    tech('report_unit_cost_provenance', 'inseason.transferWhyWorkbook.tech.unitCostProvenance', 'text', 'inseason.columnGuide.transferWhyWorkbook.report_unit_cost_provenance', { display: costProvenanceWords }),
    tech('report_unit_cost_valid_from', 'inseason.transferWhyWorkbook.tech.unitCostValidFrom', 'date', 'inseason.columnGuide.transferWhyWorkbook.report_unit_cost_valid_from'),
    tech('report_retail_price', 'inseason.transferWhyWorkbook.tech.retailPrice', 'money', 'inseason.columnGuide.transferWhyWorkbook.report_retail_price'),
    tech('report_retail_price_includes_vat', 'inseason.transferWhyWorkbook.tech.retailPriceIncludesVat', 'text', 'inseason.columnGuide.transferWhyWorkbook.report_retail_price_includes_vat', { display: includesVatWords }),
    tech('report_retail_price_vat_rate', 'inseason.transferWhyWorkbook.tech.retailPriceVatRate', 'text', 'inseason.columnGuide.transferWhyWorkbook.report_retail_price_vat_rate'),
    tech('report_retail_price_kind', 'inseason.transferWhyWorkbook.tech.retailPriceKind', 'text', 'inseason.columnGuide.transferWhyWorkbook.report_retail_price_kind', { display: retailKindWords }),
    tech('report_retail_price_provenance', 'inseason.transferWhyWorkbook.tech.retailPriceProvenance', 'text', 'inseason.columnGuide.transferWhyWorkbook.report_retail_price_provenance', { display: priceProvenanceWords }),
    tech('report_retail_price_valid_from', 'inseason.transferWhyWorkbook.tech.retailPriceValidFrom', 'date', 'inseason.columnGuide.transferWhyWorkbook.report_retail_price_valid_from'),
    reason('value_at_cost_absent_reason', 'inseason.transferWhyWorkbook.column.valueAtCost', 'inseason.columnGuide.transferWhyWorkbook.value_at_cost_absent_reason'),
    reason('value_at_retail_absent_reason', 'inseason.transferWhyWorkbook.column.valueAtRetail', 'inseason.columnGuide.transferWhyWorkbook.value_at_retail_absent_reason'),
  ];
}

function valueTechnicalCells(row: TransferExplanationReportRow, report: TransferReportRow | undefined): ExportCell[] {
  const cost = unitCostOf(report);
  const price = retailPriceOf(report);
  const money = (raw: string | null | undefined, currency: string | null | undefined): ExportCell => (
    raw === null || raw === undefined || currency === null || currency === undefined ? NOT_AVAILABLE : { kind: 'money', raw: Number(raw), currency }
  );
  const date = (iso: string | null | undefined): ExportCell => (iso === null || iso === undefined ? NOT_AVAILABLE : { kind: 'date', iso });
  const includesVat = given(price?.retail_price_includes_vat);
  return [
    money(cost?.unit_cost, cost?.unit_cost_currency),
    text(cost?.unit_cost_provenance),
    date(cost?.unit_cost_valid_from),
    money(price?.retail_price, price?.retail_price_currency),
    includesVat === null ? NOT_AVAILABLE : { kind: 'text', value: String(includesVat) },
    text(price?.retail_price_vat_rate),
    text(price?.retail_price_kind),
    text(price?.retail_price_provenance),
    date(price?.retail_price_valid_from),
    valueReasonCell(valueAtCost(row, report)),
    valueReasonCell(valueAtRetail(row, report)),
  ];
}

/**
 * The report's fields the business sheet already shows, so the «Tecnico» sheet does not repeat
 * them: the doors' and the article's codes, the size's name, the pairs, the money and its
 * currency (said once in the headers), the decided pairs, the doors' weeks of cover.
 */
const SHOWN_ON_BUSINESS_SHEET: ReadonlySet<keyof TransferExplanationReportRow> = new Set([
  'donor_store_key', 'recipient_store_key', 'article_key', 'size_code', 'units', 'lane_cost', 'run_damage',
  'net_benefit', 'currency_code', 'decided_units', 'donor_weeks_of_cover', 'recipient_weeks_of_cover',
]);

/** Every other explanation field, to «Tecnico» — the same field, header and cell as the CSV's. */
const EXPLANATION_TECHNICAL = TRANSFER_WHY_FIELDS
  .map((field, index) => ({ field, index }))
  .filter(({ field }) => !SHOWN_ON_BUSINESS_SHEET.has(field.field));

const isReasonField = (field: string): boolean => field.endsWith('_absent_reason');

/** The transfers report's fields the business sheet does not show, to «Tecnico». */
const REPORT_TECHNICAL: readonly {
  readonly field: keyof TransferReportRow;
  readonly kind: ExportColumn['kind'];
  readonly headerKey?: string;
  /** What the field means, for the column guide; an absent reason's is the shared one (`docs/adr/9045`). */
  readonly descriptionKey?: string;
  /** For an absent reason: the business column whose «n.d.» it explains. */
  readonly explains?: string;
}[] = [
  {
    field: 'as_of', kind: 'date', headerKey: 'inseason.transferWhyWorkbook.tech.asOf',
    descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.report_as_of',
  },
  {
    field: 'sales_window_start', kind: 'date', headerKey: 'inseason.transferWhyWorkbook.tech.salesWindowStart',
    descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.report_sales_window_start',
  },
  {
    field: 'sales_window_end', kind: 'date', headerKey: 'inseason.transferWhyWorkbook.tech.salesWindowEnd',
    descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.report_sales_window_end',
  },
  {
    field: 'donor_sales_l2w_units', kind: 'integer', headerKey: 'inseason.transferWhyWorkbook.tech.donorSalesL2w',
    descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.report_donor_sales_l2w_units',
  },
  {
    field: 'recipient_sales_l2w_units', kind: 'integer', headerKey: 'inseason.transferWhyWorkbook.tech.recipientSalesL2w',
    descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.report_recipient_sales_l2w_units',
  },
  {
    field: 'donor_in_transit_units', kind: 'integer', headerKey: 'inseason.transferWhyWorkbook.tech.donorInTransit',
    descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.report_donor_in_transit_units',
  },
  {
    field: 'source_major_category', kind: 'text', headerKey: 'inseason.transferWhyWorkbook.tech.majorCategory',
    descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.report_source_major_category',
  },
  {
    field: 'source_sub_category', kind: 'text', headerKey: 'inseason.transferWhyWorkbook.tech.subCategory',
    descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.report_source_sub_category',
  },
  { field: 'donor_store_name_absent_reason', kind: 'text', explains: 'inseason.transferWhyWorkbook.column.donorName' },
  { field: 'recipient_store_name_absent_reason', kind: 'text', explains: 'inseason.transferWhyWorkbook.column.recipientName' },
  { field: 'article_name_absent_reason', kind: 'text', explains: 'inseason.transferWhyWorkbook.column.articleName' },
  { field: 'brand_absent_reason', kind: 'text', explains: 'inseason.transferWhyWorkbook.column.brand' },
  { field: 'department_family_absent_reason', kind: 'text', explains: 'inseason.transferWhyWorkbook.column.departmentFamily' },
  { field: 'source_category_absent_reason', kind: 'text', explains: 'inseason.transferWhyWorkbook.column.category' },
  { field: 'donor_on_hand_before_absent_reason', kind: 'text', explains: 'inseason.transferWhyWorkbook.column.donorBefore' },
  { field: 'donor_on_hand_after_absent_reason', kind: 'text', explains: 'inseason.transferWhyWorkbook.column.donorAfter' },
  { field: 'donor_sales_l4w_absent_reason', kind: 'text', explains: 'inseason.transferWhyWorkbook.column.donorSalesL4w' },
  { field: 'recipient_on_hand_before_absent_reason', kind: 'text', explains: 'inseason.transferWhyWorkbook.column.recipientBefore' },
  { field: 'recipient_on_hand_after_absent_reason', kind: 'text', explains: 'inseason.transferWhyWorkbook.column.recipientAfter' },
  { field: 'recipient_in_transit_absent_reason', kind: 'text', explains: 'inseason.transferWhyWorkbook.column.recipientInTransit' },
  { field: 'recipient_sales_l4w_absent_reason', kind: 'text', explains: 'inseason.transferWhyWorkbook.column.recipientSalesL4w' },
  { field: 'risk_absent_reason', kind: 'text', explains: 'inseason.transferWhyWorkbook.column.risk' },
];

/** A report field to «Tecnico» under its own column key, so it does not clash with the explanation's. */
const reportKey = (field: string): string => `report_${field}`;

function technicalColumns(translate: Translate): ExportColumn[] {
  const explanation = EXPLANATION_TECHNICAL.map(({ field }): ExportColumn => ({
    key: field.field,
    headerKey: field.headerKey,
    descriptionKey: field.descriptionKey,
    kind: field.kind === 'boolean' ? 'text' : field.kind,
    technical: true,
    ...(field.field === 'transfer_id' ? { identifies: true } : {}),
    ...(isReasonField(field.field) ? { display: whyReasonWords } : {}),
  }));
  const report = REPORT_TECHNICAL.map(({ field, kind, headerKey, descriptionKey, explains }): ExportColumn => ({
    key: reportKey(field),
    headerKey: headerKey ?? 'inseason.transferWhyWorkbook.tech.absentReason',
    descriptionKey: descriptionKey ?? 'inseason.columnGuide.transferWhyWorkbook.reportAbsentReason',
    kind,
    technical: true,
    ...(explains === undefined ? {} : {
      headerValues: { field: translate(explains) },
      descriptionValues: { field: translate(explains) },
      display: whyReasonWords,
    }),
  }));
  return [
    ...explanation,
    ...report,
    ...valueTechnicalColumns(translate),
    {
      key: 'report_joined', headerKey: 'inseason.transferWhyWorkbook.tech.reportJoined', kind: 'text', technical: true,
      descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.report_joined',
    },
  ];
}

function reportCell(row: TransferReportRow | undefined, field: keyof TransferReportRow, kind: ExportColumn['kind']): ExportCell {
  if (row === undefined) return NOT_AVAILABLE;
  const value = row[field] as string | number | null;
  if (value === null || value === '') return kind === 'text' ? BLANK : NOT_AVAILABLE;
  if (kind === 'date') return { kind: 'date', iso: String(value) };
  if (kind === 'integer') return { kind: 'integer', raw: Number(value) };
  return { kind: 'text', value: String(value) };
}

function transferRowCells(
  row: TransferExplanationReportRow, report: TransferReportRow | undefined, reasonLabel: (code: string) => string, translate: Translate,
  deciders: TransferDeciders | undefined,
): ExportCell[] {
  const decision = transferDecisionCode(row);
  const money = (raw: number): ExportCell => ({ kind: 'money', raw, currency: row.currency_code });
  const csv = transferWhyCells(row);
  const superseded = row.superseded_by_run_id !== null || decision === SUPERSEDED;
  const atRetail = valueAtRetail(row, report);
  const retail = valueCell(atRetail);
  return [
    { kind: 'label', value: translate(dispositionStateKey(decision)), code: decision ?? PENDING },
    text(report?.donor_store_name),
    { kind: 'text', value: row.donor_store_key },
    text(report?.recipient_store_name),
    { kind: 'text', value: row.recipient_store_key },
    text(report?.article_name),
    { kind: 'text', value: row.article_key },
    text(report?.brand),
    text(report?.department_family),
    text(report?.source_category),
    text(row.size_code),
    { kind: 'integer', raw: row.units },
    integer(row.decided_units),
    valueCell(valueAtCost(row, report)),
    retail,
    retailBasisCell(atRetail, translate),
    money(row.net_benefit),
    money(row.lane_cost),
    money(row.run_damage),
    { kind: 'text', value: transferReasonSentence(row, translate) },
    integer(report?.donor_on_hand_before),
    integer(report?.donor_on_hand_after),
    integer(report?.donor_sales_l4w_units),
    number(row.donor_weeks_of_cover),
    integer(report?.recipient_on_hand_before),
    integer(report?.recipient_on_hand_after),
    integer(report?.recipient_in_transit_units),
    integer(report?.recipient_sales_l4w_units),
    number(row.recipient_weeks_of_cover),
    text(report?.risk),
    { kind: 'label', value: translate(toPurposeKey(row.purpose)), code: row.purpose },
    row.disposition_reason_code === null ? BLANK : { kind: 'text', value: reasonLabel(row.disposition_reason_code) },
    row.disposition_decided_at === null ? BLANK : { kind: 'date', iso: row.disposition_decided_at },
    transferDecidedByCell(row, deciders),
    { kind: 'text', value: superseded ? 'true' : 'false' },
    // «Tecnico»: the explanation's own cells, exactly as the CSV writes them, then the report's.
    ...EXPLANATION_TECHNICAL.map(({ index }) => csv[index] ?? BLANK),
    ...REPORT_TECHNICAL.map(({ field, kind }) => reportCell(report, field, kind)),
    ...valueTechnicalCells(row, report),
    { kind: 'text', value: report === undefined ? 'false' : 'true' },
  ];
}

// ── «Riepilogo origine → destinazione» ─────────────────────────────────────────────────────────

interface Shipment {
  readonly first: TransferExplanationReportRow;
  readonly report: TransferReportRow | undefined;
  readonly pairs: Map<string, number>;
  total: number;
  netBenefit: number;
  /** The sum of the lines' values; absent once one line has none — never a partial sum. */
  atCost: LineValue | null;
  atRetail: LineValue | null;
}

/**
 * A shipment's value plus one more line's. A line with no value makes the shipment's absent, with
 * that line's reason: a partial sum would read as the shipment's value. Two currencies are not
 * added, nor two retail bases (gross and net, list and ticket): the shipment's value is then «n.d.».
 */
export function addValue(total: LineValue | null, line: LineValue | null): LineValue | null {
  if (total === null || total.kind === 'absent') return total;
  if (line === null || line.kind === 'absent') return line;
  if (total.currency !== line.currency || !sameBasis(total.basis, line.basis)) return null;
  return { ...total, raw: total.raw + line.raw };
}

function sameBasis(a: RetailBasis | undefined, b: RetailBasis | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  return a.kind === b.kind && a.includesVat === b.includesVat && Number(a.vatRate) === Number(b.vatRate);
}

/** A size's column in the summary: its name as the customer says it; every unnamed size in one. */
const UNNAMED_SIZE = '\u0000unnamed';

function summarySheet(
  ordered: readonly TransferExplanationReportRow[], reports: ReadonlyMap<string, TransferReportRow>, translate: Translate,
): WorkbookSheet {
  const names = ordered.map((row) => sizeName(row));
  const withScale = scalesMix(names);
  const sizes = new Map<string, { readonly header: string; readonly position: number | null; readonly code: string }>();
  const sizeOf = (row: TransferExplanationReportRow): string => {
    const name = sizeName(row);
    if (name.code === null) {
      if (!sizes.has(UNNAMED_SIZE)) sizes.set(UNNAMED_SIZE, { header: translate('size.absent.label'), position: null, code: '￿' });
      return UNNAMED_SIZE;
    }
    const id = `${name.scale ?? ''}\u0000${name.code}`;
    if (!sizes.has(id)) {
      sizes.set(id, {
        header: withScale && name.scale !== null ? translate('size.withScale', { scale: name.scale, code: name.code }) : name.code,
        position: name.position,
        code: name.code,
      });
    }
    return id;
  };

  const shipments = new Map<string, Shipment>();
  for (const row of ordered) {
    const key = [row.donor_store_key, row.recipient_store_key, row.article_key].join('\u0000');
    let shipment = shipments.get(key);
    if (shipment === undefined) {
      const report = reports.get(row.transfer_id);
      shipment = {
        first: row, report, pairs: new Map(), total: 0, netBenefit: 0,
        atCost: valueAtCost(row, report), atRetail: valueAtRetail(row, report),
      };
      shipments.set(key, shipment);
    } else {
      const report = reports.get(row.transfer_id);
      shipment.atCost = addValue(shipment.atCost, valueAtCost(row, report));
      shipment.atRetail = addValue(shipment.atRetail, valueAtRetail(row, report));
    }
    const size = sizeOf(row);
    shipment.pairs.set(size, (shipment.pairs.get(size) ?? 0) + row.units);
    shipment.total += row.units;
    shipment.netBenefit += row.net_benefit;
  }

  const order = [...sizes.entries()].sort(([, a], [, b]) => (
    (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER) || a.code.localeCompare(b.code)
  ));
  const sorted = [...shipments.values()].sort((a, b) => (
    a.first.donor_store_key.localeCompare(b.first.donor_store_key)
    || a.first.recipient_store_key.localeCompare(b.first.recipient_store_key)
    || a.first.article_key.localeCompare(b.first.article_key)
  ));

  // The names and codes mean what they mean on «Trasferimenti»; the pairs and the benefit are the shipment's sums.
  const describedAs = (key: string): ExportColumn => {
    const shown = BUSINESS_COLUMNS.find((entry) => entry.key === key);
    if (shown === undefined) throw new Error(`no business column "${key}"`);
    return column(key, shown.headerKey, shown.kind, shown.descriptionKey === undefined ? {} : { descriptionKey: shown.descriptionKey });
  };
  const columns: ExportColumn[] = [
    { ...describedAs('donor_store_name'), freeze: true },
    { ...describedAs('donor_store_key'), freeze: true },
    describedAs('recipient_store_name'),
    describedAs('recipient_store_key'),
    describedAs('article_name'),
    describedAs('article_key'),
    ...order.map(([, size], i): ExportColumn => ({
      key: `size_${i + 1}`, headerKey: 'inseason.transferWhyWorkbook.column.size', header: size.header,
      kind: 'integer', total: 'sum', zeroAsBlank: true,
      descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.summarySizePairs',
    })),
    column('total_pairs', 'inseason.transferWhyWorkbook.column.totalProposedPairs', 'integer', {
      total: 'sum', descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.summaryTotalPairs',
    }),
    column('value_at_cost', 'inseason.transferWhyWorkbook.column.valueAtCost', 'money', {
      ...MONEY, descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.summaryValueAtCost',
    }),
    column('value_at_retail', 'inseason.transferWhyWorkbook.column.valueAtRetail', 'money', {
      ...MONEY, descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.summaryValueAtRetail',
    }),
    column('retail_price_basis', 'inseason.transferWhyWorkbook.column.retailPriceBasis', 'text', {
      descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.summaryRetailPriceBasis',
    }),
    column('net_benefit', 'inseason.transferWhyWorkbook.column.netBenefit', 'money', {
      ...MONEY, descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.summaryNetBenefit',
    }),
  ];
  const rows: ExportCell[][] = sorted.map((shipment) => [
    text(shipment.report?.donor_store_name),
    { kind: 'text', value: shipment.first.donor_store_key },
    text(shipment.report?.recipient_store_name),
    { kind: 'text', value: shipment.first.recipient_store_key },
    text(shipment.report?.article_name),
    { kind: 'text', value: shipment.first.article_key },
    ...order.map(([id]): ExportCell => {
      const pairs = shipment.pairs.get(id);
      return pairs === undefined ? { kind: 'missing', state: 'counted-zero' } : { kind: 'integer', raw: pairs };
    }),
    { kind: 'integer', raw: shipment.total },
    valueCell(shipment.atCost),
    valueCell(shipment.atRetail),
    retailBasisCell(shipment.atRetail, translate),
    { kind: 'money', raw: shipment.netBenefit, currency: shipment.first.currency_code },
  ]);
  return { titleKey: 'inseason.transferWhyWorkbook.sheet.summary', columns, rows: () => rows };
}

/**
 * The workbook's two data sheets over the explanation rows already read, filtered and ranked, the
 * transfers report's rows of the run and the reason vocabulary (`null` when it could not be read:
 * the reason is then its code). Pure.
 */
export function transferWhyWorkbook(
  ordered: readonly TransferExplanationReportRow[],
  report: readonly TransferReportRow[],
  reasons: readonly TransferReasonOption[] | null,
  translate: Translate,
  /** Who decided each transfer, by `transfer_id`; absent: «Decided by» not reported. */
  deciders?: TransferDeciders,
): readonly WorkbookSheet[] {
  const byTransfer = new Map(report.map((row) => [row.transfer_id, row]));
  const reasonLabel = reasonLabelOf(reasons ?? undefined);
  const columns = [...BUSINESS_COLUMNS, ...technicalColumns(translate)];
  const rows = ordered.map((row) => transferRowCells(row, byTransfer.get(row.transfer_id), reasonLabel, translate, deciders));
  return [
    { titleKey: 'inseason.transferWhyWorkbook.sheet.transfers', columns, rows: () => rows },
    summarySheet(ordered, byTransfer, translate),
  ];
}
